<script setup>
import { useNotify } from '@/composables/notify'
import { useConfirmDialog } from '@/composables/confirmDialog'
import AsyncButton from './AsyncButton.vue'
import i18n from '@/i18n'
import api from '@/lib/api'
import outlook from '@/outlook'

const props = defineProps({
  visitor: { type: Object, required: true },
  flexopusEventId: { type: Number, required: true },
  settings: { type: Object, required: true },
})

const emit = defineEmits(['update'])

const { success } = useNotify()
const { confirm } = useConfirmDialog()
const { isToday } = outlook.useAppointmentSchedule()

async function addInvitation () {
  if (!await confirm({
    text: i18n.t('Visitors.ConfirmAddVisitorInvitation'),
  })) {
    return
  }
  await api.createVisitorInvitation({
    event_id: props.flexopusEventId,
    visitor_ids: [props.visitor.id],
  })
  emit('update')
  success(i18n.t('Success.InvitationCreated'))
}

async function checkIn () {
  await api.checkInVisitorInvitation(props.visitor.invitation.id)
  emit('update')
  success(i18n.t('Success.InvitationCheckedIn'))
}

async function checkOut () {
  await api.checkOutVisitorInvitation(props.visitor.invitation.id)
  emit('update')
  success(i18n.t('Success.InvitationCheckedOut'))
}

async function resendEmail () {
  await api.resendVisitorInvitationEmail(props.visitor.invitation.id)
  emit('update')
  success(i18n.t('Success.InvitationEmailResent'))
}

async function removeInvitation () {
  if (!await confirm({
    text: i18n.t('Visitors.ConfirmRemoveVisitorInvitation'),
    danger: true,
  })) {
    return
  }

  await api.deleteVisitorInvitation(props.visitor.invitation.id)
  emit('update')
  success(i18n.t('Success.InvitationDeleted'))
}
</script>

<template>
  <div>
    <div class="text-base font-medium my-4">
      {{ $t('Visitors.Details.VisitorDetailsTitle') }}
    </div>
    <div class="text-xs">
      <div class="flex justify-between mb-2">
        <span
          class="font-medium"
        >
          {{ $t('Visitors.Details.Name') }}
        </span>
        <span class="text-neutral-500">
          {{ visitor.name }}
        </span>
      </div>
      <div class="flex justify-between mb-2">
        <span
          class="font-medium"
        >
          {{ $t('Visitors.Details.Email') }}
        </span>
        <span class="text-neutral-500">
          {{ visitor.email }}
        </span>
      </div>
      <div class="flex justify-between mb-2">
        <span
          class="font-medium"
        >
          {{ $t('Visitors.Details.VisitorStatus') }}
        </span>
        <span
          class="inline-flex gap-1 text-white px-1.5 text-xs rounded font-medium text-center"
          :class="visitor.verified ? 'bg-green-700' : 'bg-amber-500'"
        >
          <template v-if="visitor.verified">
            {{ $t('Visitors.Details.Verified') }}
          </template>
          <template v-else>
            {{ $t('Visitors.Details.Unverified') }}
          </template>
        </span>
      </div>
    </div>
    <template v-if="visitor.invitation">
      <div class="divider my-4" />
      <div class="text-base font-medium mb-4">
        {{ $t('Visitors.Details.InvitationDetailsTitle') }}
      </div>
      <div class="text-xs">
        <div
          v-if="visitor.invitation.code"
          class="mb-2 flex flex-col gap-2"
        >
          <span
            class="font-medium"
          >
            {{ $t('Visitors.Details.Code') }}
          </span>
          <div class="border border-gray-300 bg-gray-100 rounded-md p-2 flex gap-2 font-mono text-xl justify-around">
            <template
              v-for="i in 9"
            >
              {{ String(visitor.invitation.code)[i-1] }}
              <template
                v-if="i % 3 === 0 && i != 9"
              >
                &nbsp;
              </template>
            </template>
          </div>
        </div>
        <div class="flex justify-between mb-2">
          <span
            class="font-medium"
          >
            {{ $t('Visitors.Details.CheckInTime') }}
          </span>
          <span class="text-neutral-500">
            {{ visitor.invitation.checkin_time
              ? new Date(visitor.invitation.checkin_time).toLocaleString()
              : '-' }}
          </span>
        </div>
        <div class="flex justify-between mb-2">
          <span
            class="font-medium"
          >
            {{ $t('Visitors.Details.CheckOutTime') }}
          </span>
          <span class="text-neutral-500">
            {{ visitor.invitation.checkout_time
              ? new Date(visitor.invitation.checkout_time).toLocaleString()
              : '-' }}
          </span>
        </div>
      </div>
    </template>

    <div class="divider my-4" />

    <AsyncButton
      v-if="!visitor.invitation"
      class="btn btn-primary mb-2 w-full"
      @click="addInvitation"
    >
      {{ $t('Visitors.Details.AddInvitation') }}
    </AsyncButton>
    <template v-else>
      <template v-if="isToday && settings.visitor_user_checkin">
        <AsyncButton
          v-if="!visitor.invitation.checkin_time"
          class="btn btn-secondary mb-2 w-full"
          :disabled="!visitor.verified || visitor.invitation.checkin_time"
          @click="checkIn"
        >
          {{ $t('Visitors.Details.CheckIn') }}
        </AsyncButton>
        <AsyncButton
          v-if="visitor.invitation.checkin_time && !visitor.invitation.checkout_time"
          class="btn btn-secondary mb-2 w-full"
          @click="checkOut"
        >
          {{ $t('Visitors.Details.CheckOut') }}
        </AsyncButton>
      </template>
      <AsyncButton
        class="btn btn-secondary mb-2 w-full"
        @click="resendEmail"
      >
        {{ $t('Visitors.Details.ResendEmail') }}
      </AsyncButton>
      <AsyncButton
        class="btn btn-danger mb-2 w-full"
        @click="removeInvitation"
      >
        {{ $t('Visitors.Details.RemoveInvitation') }}
      </AsyncButton>
    </template>
  </div>
</template>
